import { supabase } from '../lib/supabase';

export const notificationPreferencesService = {
  // Get user notification preferences
  async getUserPreferences() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('notification_preferences')?.select('*')?.eq('user_id', user?.id)?.single();

    if (error) {
      if (error?.code === 'PGRST116') {
        // No preferences found, create default
        return this.createDefaultPreferences();
      }
      throw error;
    }

    return this.convertToServiceFormat(data);
  },

  // Create default preferences
  async createDefaultPreferences() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('notification_preferences')?.insert({
        user_id: user?.id
      })?.select()?.single();

    if (error) throw error;

    return this.convertToServiceFormat(data);
  },

  // Update preferences
  async updatePreferences(preferences) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const dbFormat = this.convertToDatabaseFormat(preferences);

    const { data, error } = await supabase?.from('notification_preferences')?.update(dbFormat)?.eq('user_id', user?.id)?.select()?.single();

    if (error) throw error;

    return this.convertToServiceFormat(data);
  },

  // Update specific category preferences
  async updateCategoryPreferences(category, preferences) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const updates = {};
    Object.keys(preferences)?.forEach(key => {
      const dbKey = this.toSnakeCase(key);
      updates[dbKey] = preferences?.[key];
    });

    const { data, error } = await supabase?.from('notification_preferences')?.update(updates)?.eq('user_id', user?.id)?.select()?.single();

    if (error) throw error;

    return this.convertToServiceFormat(data);
  },

  // Bulk enable/disable category
  async bulkToggleCategory(category, enabled) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const categoryMapping = {
      trading: ['email_new_offers', 'sms_new_offers', 'push_new_offers', 
                'email_price_changes', 'sms_price_changes', 'push_price_changes',
                'email_transaction_confirmations', 'sms_transaction_confirmations', 'push_transaction_confirmations'],
      wish: ['email_matching_wishes', 'sms_matching_wishes', 'push_matching_wishes',
             'email_negotiation_updates', 'sms_negotiation_updates', 'push_negotiation_updates',
             'email_proposal_responses', 'sms_proposal_responses', 'push_proposal_responses'],
      community: ['email_forum_replies', 'sms_forum_replies', 'push_forum_replies',
                  'email_mentions', 'sms_mentions', 'push_mentions',
                  'email_achievements', 'sms_achievements', 'push_achievements'],
      system: ['email_maintenance_alerts', 'sms_maintenance_alerts', 'push_maintenance_alerts',
               'email_policy_changes', 'sms_policy_changes', 'push_policy_changes',
               'email_security_notifications', 'sms_security_notifications', 'push_security_notifications']
    };

    const fields = categoryMapping?.[category];
    if (!fields) throw new Error('Invalid category');

    const updates = {};
    fields?.forEach(field => {
      updates[field] = enabled;
    });

    const { data, error } = await supabase?.from('notification_preferences')?.update(updates)?.eq('user_id', user?.id)?.select()?.single();

    if (error) throw error;

    return this.convertToServiceFormat(data);
  },

  // Update advanced settings
  async updateAdvancedSettings(settings) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const dbFormat = {
      notification_frequency: settings?.notificationFrequency,
      quiet_hours_start: settings?.quietHoursStart,
      quiet_hours_end: settings?.quietHoursEnd,
      priority_urgent_only: settings?.priorityUrgentOnly
    };

    const { data, error } = await supabase?.from('notification_preferences')?.update(dbFormat)?.eq('user_id', user?.id)?.select()?.single();

    if (error) throw error;

    return this.convertToServiceFormat(data);
  },

  // Email Digest Settings Methods
  async getDigestSettings() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('notification_preferences')
      ?.select('digest_enabled, digest_frequency, digest_day_of_week, digest_time, digest_categories, last_digest_sent_at')
      ?.eq('user_id', user?.id)
      ?.single();

    if (error) throw error;

    return {
      digestEnabled: data?.digest_enabled,
      digestFrequency: data?.digest_frequency,
      digestDayOfWeek: data?.digest_day_of_week,
      digestTime: data?.digest_time,
      digestCategories: data?.digest_categories || { trading: true, wish: true, community: true, system: false },
      lastDigestSentAt: data?.last_digest_sent_at
    };
  },

  async updateDigestSettings(settings) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const dbFormat = {
      digest_enabled: settings?.digestEnabled,
      digest_frequency: settings?.digestFrequency,
      digest_day_of_week: settings?.digestDayOfWeek,
      digest_time: settings?.digestTime,
      digest_categories: settings?.digestCategories
    };

    const { data, error } = await supabase
      ?.from('notification_preferences')
      ?.update(dbFormat)
      ?.eq('user_id', user?.id)
      ?.select('digest_enabled, digest_frequency, digest_day_of_week, digest_time, digest_categories, last_digest_sent_at')
      ?.single();

    if (error) throw error;

    return {
      digestEnabled: data?.digest_enabled,
      digestFrequency: data?.digest_frequency,
      digestDayOfWeek: data?.digest_day_of_week,
      digestTime: data?.digest_time,
      digestCategories: data?.digest_categories,
      lastDigestSentAt: data?.last_digest_sent_at
    };
  },

  async toggleDigestCategory(category, enabled) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // First get current digest categories
    const { data: current } = await supabase
      ?.from('notification_preferences')
      ?.select('digest_categories')
      ?.eq('user_id', user?.id)
      ?.single();

    const categories = current?.digest_categories || { trading: true, wish: true, community: true, system: false };
    categories[category] = enabled;

    const { data, error } = await supabase
      ?.from('notification_preferences')
      ?.update({ digest_categories: categories })
      ?.eq('user_id', user?.id)
      ?.select('digest_categories')
      ?.single();

    if (error) throw error;

    return data?.digest_categories;
  },

  // Helper: Convert snake_case to camelCase
  toCamelCase(str) {
    return str?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
  },

  // Helper: Convert camelCase to snake_case
  toSnakeCase(str) {
    return str?.replace(/([A-Z])/g, '_$1')?.toLowerCase();
  },

  // Helper: Convert DB format to service format
  convertToServiceFormat(data) {
    if (!data) return null;

    const result = {};
    Object.keys(data)?.forEach(key => {
      result[this.toCamelCase(key)] = data?.[key];
    });
    return result;
  },

  // Helper: Convert service format to DB format
  convertToDatabaseFormat(data) {
    const result = {};
    Object.keys(data)?.forEach(key => {
      if (key !== 'id' && key !== 'createdAt' && key !== 'updatedAt') {
        result[this.toSnakeCase(key)] = data?.[key];
      }
    });
    return result;
  }
};